import { Injectable } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { ProblemDetails } from "../../models/home/dtos";
import { UxError } from "../../models/ux-error.model";

@Injectable({ providedIn: "root" })
export class ProblemDetailsHandler {
  handle(error: HttpErrorResponse): Observable<UxError> {
    const payload = this.extractProblemDetails(error);
    const retryAfterSeconds = this.getRetryAfterSeconds(error, payload);

    const status = error.status || payload?.status || undefined;
    const title = payload?.title ?? this.defaultTitle(status);
    const message = payload?.detail ?? this.defaultMessage(status);

    return of({
      status,
      title,
      message,
      actionLabel: "Reintentar",
      retryAfterSeconds
    });
  }

  private extractProblemDetails(error: HttpErrorResponse): ProblemDetails | null {
    const payload = error.error as ProblemDetails | undefined;
    if (payload && (payload.title || payload.detail || payload.status)) {
      return payload;
    }
    return null;
  }

  private getRetryAfterSeconds(error: HttpErrorResponse, payload: ProblemDetails | null): number | undefined {
    const headerValue = error.headers?.get("Retry-After");
    if (headerValue) {
      const seconds = Number(headerValue);
      if (!Number.isNaN(seconds)) {
        return seconds;
      }
    }

    const retryHint = (payload as { retryAfterHintSeconds?: number } | null)?.retryAfterHintSeconds;
    if (typeof retryHint === "number") {
      return retryHint;
    }

    return undefined;
  }

  private defaultTitle(status?: number): string {
    if (!status) {
      return "Error al cargar datos";
    }

    if (status === 401) {
      return "Sesión expirada";
    }

    if (status === 404) {
      return "Información no encontrada";
    }

    if (status === 410) {
      return "Recurso no disponible";
    }

    if (status === 503) {
      return "Sistema ocupado";
    }

    return "No pudimos completar la solicitud";
  }

  private defaultMessage(status?: number): string {
    if (!status) {
      return "Ocurrió un problema al obtener la información.";
    }

    if (status === 401) {
      return "Tu sesión expiró. Inicia sesión nuevamente.";
    }

    if (status === 404) {
      return "No encontramos datos para este bloque.";
    }

    if (status === 410) {
      return "Este recurso ya no está disponible.";
    }

    if (status === 503) {
      return "El sistema está procesando. Intentaremos de nuevo en breve.";
    }

    return "Intenta nuevamente en unos segundos.";
  }
}
